import React from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  FileText, 
  ShoppingBag, 
  Image, 
  MessageSquare, 
  Users,
  DollarSign,
  TrendingUp
} from 'lucide-react';

const AdminDashboard = () => {
  const { stats } = useOutletContext();

  const statCards = [
    { label: 'Blog Posts', value: stats?.blog_posts || 0, icon: FileText, color: 'bg-blue-500' },
    { label: 'Products', value: stats?.products || 0, icon: ShoppingBag, color: 'bg-purple-500' },
    { label: 'Images', value: stats?.images || 0, icon: Image, color: 'bg-green-500' },
    { label: 'Orders', value: stats?.orders || 0, icon: DollarSign, color: 'bg-yellow-500' },
    { label: 'New Contacts', value: stats?.new_contacts || 0, icon: MessageSquare, color: 'bg-red-500' },
    { label: 'Subscribers', value: stats?.subscribers || 0, icon: Users, color: 'bg-terracota' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-2xl font-serif text-white mb-2">Dashboard</h1>
        <p className="text-stone-400">Welcome back! Here's an overview of your content.</p>
      </div>

      {/* Revenue Card */}
      <div className="bg-gradient-to-r from-terracota to-orange-600 rounded-xl p-6 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm mb-1">Total Revenue</p>
            <p className="text-3xl font-bold text-white">
              ${(stats?.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div 
              key={stat.label}
              className="bg-stone-800 rounded-xl p-6 border border-stone-700"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
              <p className="text-stone-400 text-sm">{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-stone-800 rounded-xl p-6 border border-stone-700">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <a href="/admin/blog" className="bg-stone-700 hover:bg-stone-600 rounded-lg p-4 text-center transition-colors">
            <FileText className="w-6 h-6 text-terracota mx-auto mb-2" />
            <p className="text-white text-sm">New Blog Post</p>
          </a>
          <a href="/admin/products" className="bg-stone-700 hover:bg-stone-600 rounded-lg p-4 text-center transition-colors">
            <ShoppingBag className="w-6 h-6 text-terracota mx-auto mb-2" />
            <p className="text-white text-sm">New Product</p>
          </a>
          <a href="/admin/images" className="bg-stone-700 hover:bg-stone-600 rounded-lg p-4 text-center transition-colors">
            <Image className="w-6 h-6 text-terracota mx-auto mb-2" />
            <p className="text-white text-sm">Upload Image</p>
          </a>
          <a href="/admin/contacts" className="bg-stone-700 hover:bg-stone-600 rounded-lg p-4 text-center transition-colors">
            <MessageSquare className="w-6 h-6 text-terracota mx-auto mb-2" />
            <p className="text-white text-sm">View Messages</p>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
